import React, { useState } from "react";
import { useParams, useHistory } from "react-router-dom";

import "./MyData.scss";
import { Typography } from "../../../components/__common/Typography/Typography";
import { LinkButton, TTSButton } from "../../../components/__common/_controls";
import { Svg, SVG_ICONS } from "../../../components/__common/Svg/Svg";
import { Summary } from "./Sections/Summary/Summary";
import { ManageApps } from "./Sections/ManageApps/ManageApps";
import { UsageGuide } from "./UsageGuide/UsageGuide";
import { useSelector } from "react-redux";
import { ApplicationState } from "../../../store";
import { MyDataState } from "../../../store/reducers/mydata-reducer";
import Loading from "../../../assets/images/gif/loading_transparent.gif";
import LoadingHC from "../../../assets/images/gif/loading_transparent_HC.gif";
import { Image } from "../../../components/__common/Image";
import { useEffect } from "react";
import { useMyDataHooks } from "../../../hooks/mydata-hooks";
import { RequestDetailsModal } from "./RequestDetailsModal/RequestDetailsModal";
import { ROUTES } from "../../../configs/routes";
import { GlobalSiteLinkPopup } from "../../../components/GlobalSiteLinkPopup/GlobalSiteLinkPopup";
import { Trans, useTranslation } from "react-i18next";
import { UserState } from "../../../store/reducers/user-reducer";
import triggerGAEvent from "../../../utils/UtilsTriggerGAEvent";
import {
  EVENT_CATEGORY_MY_DATA,
  EVENT_MY_DATA_ACTION_MORE_INFORMATION,
} from "../../../constants/analytics";
import { HelpTipsPoupup } from "../../../components/HelpTipsPopup/HelpTipsPopup";
import { Container } from "../../../components/Grid/Grid";
import { URLS } from "../../../constants/urls";
import { Cookie } from "../../../services/storage";
import { REQUEST_NUMBER } from "../../../constants/storage";
import { UtilsUser } from "../../../utils/UtilsUser";
import { LayoutState } from "../../../store/reducers/layout-reducer";
import LoadingFC from "../../../components/__common/Loading/Loading";
import { UtilsHome } from "../../../utils/UtilsHome";

export const MyData = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const params: any = useParams();
  const { loadSummary, closeGlobalSiteLinkPopup } = useMyDataHooks();
  const [isGuideOpen, setGuideOpen] = useState<boolean>(false);
  const appMyDataState = useSelector<ApplicationState, MyDataState>(
    (state) => state.myData
  );
  const userState = useSelector<ApplicationState, UserState>(
    (state) => state.user
  );
  const layoutState = useSelector<ApplicationState, LayoutState>(
    (state) => state.layout
  );

  const { isSummaryLoading, summary, isGlobalSiteLinkOpen } = appMyDataState;
  const { isHighContrast, languageTrans, selectedCountry } = layoutState;
  const { userCountryCode } = userState;

  const cookieRequestNumber = Cookie.get(REQUEST_NUMBER);

  useEffect(
    () => {
      const currentURL = window.location.pathname;
      if (currentURL.includes(URLS.OLD_MYDATA)) {
        history.replace(URLS.MYDATA);
      } else if (
        !params.requestNumber &&
        userState.userData.countryCode !== ""
      ) {
        loadSummary();
      }
    },
    // eslint-disable-next-line
    [languageTrans, userState.userData.countryCode]
  );

  useEffect(() => {
    const countryCode = selectedCountry.code
      ? selectedCountry.code
      : userCountryCode;

    if (UtilsHome.hasLocalSite(countryCode) && !isSummaryLoading) {
      history.replace(
        `${summary.count.total_count > 0 ? ROUTES.MYDATA_HISTORY : ROUTES.ROOT}`
      );
    }
  }, [summary, selectedCountry, userCountryCode]);

  useEffect(
    () => {
      if (cookieRequestNumber && UtilsUser.isUserLoggedIn()) {
        const countryCode = selectedCountry.code
          ? selectedCountry.code
          : userCountryCode;

        history.replace(
          `${
            UtilsHome.hasLocalSite(countryCode)
              ? ROUTES.MYDATA_HISTORY
              : ROUTES.MYDATA
          }/${cookieRequestNumber}`
        );
      }
    },
    // eslint-disable-next-line
    [cookieRequestNumber, userCountryCode, selectedCountry]
  );

  useEffect(() => {
    if (isGuideOpen) {
      document.getElementById("usage-guide-modal-tts")?.focus();
    }
  }, [isGuideOpen]);

  const getCountrySpecificComponent = (
    country: string,
    isUserRestricted?: boolean
  ) => {
    if (country === "CHN") {
      return (
        <Container className="mydata__china">
          <Svg
            icon={SVG_ICONS.MYDATA_CHINA}
            width={320}
            height={320}
            className="mydata__china__icon"
          />
          <Typography variant="title3" className="mydata__china__title">
            {t("PRIVACY_MYDATA_MSG_CHINA")}
          </Typography>
          <Typography variant="body2" className="mydata__china__text">
            <Trans
              i18nKey="PRIVACY_MYDATA_MSG_CHINA_CNPP_LINK"
              components={{
                a: (
                  // eslint-disable-next-line
                  <a target="_blank" href="https://privacy.samsung.com.cn/" />
                ),
              }}
            />
          </Typography>
        </Container>
      );
    } else if (country === "KOR" && isUserRestricted) {
      return (
        <Container className="mydata__china">
          <Svg
            icon={SVG_ICONS.MYDATA_KOREA_CHILD}
            width={320}
            height={320}
            className="mydata__china__icon"
          />
          <Typography variant="title3" className="mydata__china__title">
            {t("PRIVACY_MYDATA_MSG_CHILDREN_KO")}
          </Typography>
          <Typography variant="body2" className="mydata__china__text">
            {t("PRIVACY_MYDATA_MSG_CHILDREN_KO2")}
          </Typography>
          <Typography variant="body2" className="mydata__china__text__bulleted">
            <Trans
              i18nKey="PRIVACY_MYDATA_MSG_CHILDREN_KO3"
              components={{
                a: (
                  // eslint-disable-next-line
                  <a
                    target="_blank"
                    href="https://www.samsung.com/sec/support"
                  />
                ),
              }}
            />
          </Typography>
        </Container>
      );
    } else if (country === "USA" && isUserRestricted) {
      return (
        <Container className="mydata__china">
          <Svg
            icon={SVG_ICONS.MYDATA_KOREA_CHILD}
            width={320}
            height={320}
            className="mydata__china__icon"
          />
          <Typography variant="body2" className="mydata__china__altText">
            <Trans
              i18nKey="PRIVACY_MYDATA_MSG_CHILDREN_USA"
              components={{
                a: (
                  // eslint-disable-next-line
                  <a
                    target="_blank"
                    href="https://www.samsung.com/us/privacy"
                  />
                ),
              }}
            />
          </Typography>
        </Container>
      );
    }
  };
  return isSummaryLoading ? (
    <LoadingFC />
  ) : (
    <>
      {!!params.requestNumber && (
        <RequestDetailsModal
          show={!!params.requestNumber}
          requestNumber={params.requestNumber}
          onHide={() => history.replace(ROUTES.MYDATA)}
          onCompleteCancel={loadSummary}
        />
      )}

      <div className="mydata">
        <HelpTipsPoupup />
        {isGlobalSiteLinkOpen && (
          <GlobalSiteLinkPopup onHide={closeGlobalSiteLinkPopup} />
        )}

        {isGuideOpen && (
          <UsageGuide
            open={isGuideOpen}
            onClickClose={() => setGuideOpen(false)}
          />
        )}
        <div className="mydata__title-section">
          <Typography variant="h2" ss>
            {t("PRIVACY_MYDATA_PAGE_TITLE")}
            <TTSButton
              value={
                t("PRIVACY_MYDATA_PAGE_TITLE") +
                ". " +
                t("PRIVACY_MYDATA_PAGE_DESC")
              }
            />
          </Typography>
          <Typography
            variant="body1"
            weight="w400"
            className="mydata__title-section__sub"
          >
            {t("PRIVACY_MYDATA_PAGE_DESC")}
          </Typography>
          {userState.userData.countryCode !== "CHN" && (
            <LinkButton
              to="#"
              title={t("PRIVACY_MYDATA_PAGE_BTN_GUIDE")}
              iconPosition="left"
              icon={SVG_ICONS.GUIDE}
              underlined={false}
              iconSize={24}
              size="lg"
              textClassName={"mydata__title-section__more"}
              onClick={() => {
                triggerGAEvent(
                  EVENT_CATEGORY_MY_DATA,
                  EVENT_MY_DATA_ACTION_MORE_INFORMATION
                );
                setGuideOpen(true);
              }}
            />
          )}
        </div>
        {!isSummaryLoading ? (
          ((userState.userData.countryCode === "KOR" ||
            userState.userData.countryCode === "USA") &&
            userState.userRegion.isRestricted) ||
          userState.userData.countryCode === "CHN" ? (
            getCountrySpecificComponent(
              userState.userData.countryCode,
              userState.userRegion.isRestricted
            )
          ) : (
            <Summary
              summary={summary}
              isFromChina={userState.userData.countryCode === "CHN"}
              isRestricted={userState.userRegion.isRestricted}
              saCountry={userState.userData.countryCode}
            />
          )
        ) : (
          <Image
            image={!isHighContrast ? Loading : LoadingHC}
            width={60}
            height={60}
            className="mydata__loading"
          />
        )}

        {userState.userData.countryCode !== "CHN" &&
          !userState.userRegion.isRestricted && <ManageApps />}
      </div>
    </>
  );
};
